// frontend/src/pages/admin/AdminAnalytics.js
import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/admin';
import { 
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line 
} from 'recharts';
import { FaChartPie, FaSearch, FaUsers, FaTrophy, FaSpinner } from 'react-icons/fa';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await adminApi.getDeepAnalytics();
        setData(result);
      } catch (err) {
        console.error("Failed to load analytics", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <div className="flex justify-center items-center h-screen"><FaSpinner className="animate-spin text-4xl text-primary" /></div>;
  if (!data) return <div className="text-center py-20">No data available.</div>;

  return (
    <div className="pb-10">
      <h1 className="text-2xl font-bold text-gray-800 mb-8">Deep Analytics & Reports</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* 1. REVENUE BY CATEGORY (PIE) */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <FaChartPie className="text-blue-500" /> Service Volume by Type
          </h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.revenue_by_category}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {data.revenue_by_category.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 2. TOP PRODUCTS (BAR) */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <FaTrophy className="text-yellow-500" /> Top Selling Parts
          </h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.top_products} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={100} tick={{fontSize: 12}} />
                <Tooltip />
                <Bar dataKey="sales" fill="#82ca9d" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 3. USER GROWTH (LINE) */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <FaUsers className="text-purple-500" /> User Growth (Monthly)
          </h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.user_growth}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="users" stroke="#8884d8" strokeWidth={3} activeDot={{ r: 8 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 4. SEARCH INSIGHTS (TABLE) */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <FaSearch className="text-gray-500" /> Top Search Terms
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 text-gray-500 uppercase">
                <tr>
                  <th className="p-3">Term</th>
                  <th className="p-3 text-center">Count</th>
                  <th className="p-3 text-right">Zero Results</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.top_searches.length === 0 ? (
                    <tr><td colSpan="3" className="p-4 text-center text-gray-400">No search data yet.</td></tr>
                ) : (
                    data.top_searches.map((s, idx) => (
                      <tr key={idx}>
                        <td className="p-3 font-medium text-gray-800">{s.term}</td>
                        <td className="p-3 text-center">{s.count}</td>
                        <td className={`p-3 text-right font-bold ${s.zero_results > 0 ? 'text-red-500' : 'text-gray-400'}`}>
                          {s.zero_results}
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}

export default AdminAnalytics;